/**
 * Admin Users Page
 * Dashboard for managing users in the system
 * Features: user list, search, invite, edit, delete, enable/disable
 *
 * @module pages/AdminUsersPage
 */

import { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { useToast } from '@/context/ToastContext';
import { Layout, AdminRoute } from '@/components/layout';
import { UserTable, UserModal, AdminStats } from '@/components/admin';
import Button from '@/components/common/Button';
import { User, InviteUserData, UpdateUserData } from '@/types';
import { getUsers, inviteUser, updateUser, deleteUser, enableUser, disableUser, getStats } from '@/services/adminService';

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Admin Users Page
 * Provides interface for user management with full CRUD operations
 */
export default function AdminUsersPage() {
  // State
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'invite' | 'edit'>('invite');
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string>();

  const { addToast } = useToast();

  // Debug logging
  useEffect(() => {
    logger.debug('[WALRUS] AdminUsersPage: mounted');
  }, []);

  /**
   * Fetch users on component mount
   */
  useEffect(() => {
    loadUsers();
    loadStats();
  }, []);

  /**
   * Load users from API
   */
  const loadUsers = async () => {
    logger.debug('[WALRUS] AdminUsersPage: loading users');
    try {
      setIsLoadingUsers(true);
      const data = await getUsers();
      logger.info('[WALRUS] AdminUsersPage: users loaded', { count: data.length });
      setUsers(data);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('[WALRUS] AdminUsersPage: failed to load users', { error: errorMessage });
      addToast('error', 'Failed to load users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  /**
   * Load statistics from API
   */
  const loadStats = async () => {
    logger.debug('[WALRUS] AdminUsersPage: loading stats');
    try {
      setIsLoadingStats(true);
      const data = await getStats();
      logger.info('[WALRUS] AdminUsersPage: stats loaded', { stats: data });
      setStats(data);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.warn('[WALRUS] AdminUsersPage: failed to load stats', { error: errorMessage });
      // Don't show error toast for stats - it's not critical
    } finally {
      setIsLoadingStats(false);
    }
  };

  /**
   * Open invite modal
   */
  const handleOpenInviteModal = () => {
    logger.debug('[WALRUS] AdminUsersPage: opening invite modal');
    setModalMode('invite');
    setSelectedUser(undefined);
    setIsModalOpen(true);
  };

  /**
   * Open edit modal for user
   */
  const handleEditUser = (user: User) => {
    logger.debug('[WALRUS] AdminUsersPage: opening edit modal', { userId: user.id });
    setModalMode('edit');
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  /**
   * Handle user invite submission
   */
  const handleInviteSubmit = async (data: InviteUserData) => {
    logger.debug('[WALRUS] AdminUsersPage: submitting invite', { email: data.email });
    try {
      setIsSubmitting(true);
      const newUser = await inviteUser(data);
      logger.info('[WALRUS] AdminUsersPage: user invited', { email: data.email });

      // Add new user to list
      setUsers([newUser, ...users]);
      addToast('success', `Invitation sent to ${data.email}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('[WALRUS] AdminUsersPage: invite failed', { error: errorMessage });
      addToast('error', `Failed to invite user: ${errorMessage}`);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle user edit submission
   */
  const handleEditSubmit = async (data: UpdateUserData) => {
    if (!selectedUser) return;

    logger.debug('[WALRUS] AdminUsersPage: submitting edit', { userId: selectedUser.id });
    try {
      setIsSubmitting(true);
      const updatedUser = await updateUser(selectedUser.id, data);
      logger.info('[WALRUS] AdminUsersPage: user updated', { userId: selectedUser.id });

      // Update user in list
      setUsers(users.map((u) => (u.id === selectedUser.id ? updatedUser : u)));
      addToast('success', 'User updated successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('[WALRUS] AdminUsersPage: edit failed', { error: errorMessage });
      addToast('error', `Failed to update user: ${errorMessage}`);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle user deletion
   */
  const handleDeleteUser = async (user: User) => {
    const confirmed = confirm(
      `Are you sure you want to delete ${user.email}? This action cannot be undone.`
    );

    if (!confirmed) {
      logger.debug('[WALRUS] AdminUsersPage: delete cancelled');
      return;
    }

    logger.debug('[WALRUS] AdminUsersPage: deleting user', { userId: user.id });
    try {
      setDeletingUserId(user.id);
      await deleteUser(user.id);
      logger.info('[WALRUS] AdminUsersPage: user deleted', { userId: user.id });

      // Remove user from list
      setUsers(users.filter((u) => u.id !== user.id));
      addToast('success', `${user.email} has been deleted`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('[WALRUS] AdminUsersPage: delete failed', { error: errorMessage });
      addToast('error', `Failed to delete user: ${errorMessage}`);
    } finally {
      setDeletingUserId(undefined);
    }
  };

  /**
   * Handle enable/disable user
   */
  const handleEnableDisable = async (user: User, enable: boolean) => {
    logger.debug('[WALRUS] AdminUsersPage: toggling user status', {
      userId: user.id,
      enable,
    });

    try {
      setDeletingUserId(user.id);
      if (enable) {
        await enableUser(user.id);
      } else {
        await disableUser(user.id);
      }
      logger.info('[WALRUS] AdminUsersPage: user status updated', {
        userId: user.id,
        isEnabled: enable,
      });

      // Update user in list with new enabled status
      setUsers(users.map((u) => (u.id === user.id ? { ...u, isEnabled: enable } : u)));
      addToast(
        'success',
        `${user.email} has been ${enable ? 'enabled' : 'disabled'}`
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('[WALRUS] AdminUsersPage: status change failed', {
        error: errorMessage,
      });
      addToast('error', `Failed to update user status: ${errorMessage}`);
    } finally {
      setDeletingUserId(undefined);
    }
  };

  /**
   * Handle modal submit based on mode
   */
  const handleModalSubmit = async (data: InviteUserData | UpdateUserData) => {
    if (modalMode === 'invite') {
      await handleInviteSubmit(data as InviteUserData);
    } else if (modalMode === 'edit') {
      await handleEditSubmit(data as UpdateUserData);
    }
  };

  return (
    <AdminRoute>
      <Layout>
        <div className="min-h-screen bg-walrus-white">
          {/* Page header */}
          <div className="border-b border-walrus-gray bg-walrus-cream sticky top-16 z-40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-walrus-black">Family Management</h1>
                  <p className="mt-1 text-walrus-silver">Manage family member accounts and permissions</p>
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleOpenInviteModal}
                  disabled={isSubmitting}
                >
                  Invite Family Member
                </Button>
              </div>
            </div>
          </div>

          {/* Main content */}
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Statistics */}
            {stats && (
              <div className="mb-8">
                <AdminStats stats={stats} isLoading={isLoadingStats} />
              </div>
            )}

            {/* User table section */}
            <div className="bg-walrus-cream rounded-lg border border-walrus-gray p-6">
              <h2 className="text-xl font-bold text-walrus-black mb-6">Family Members</h2>

              <UserTable
                users={users}
                isLoading={isLoadingUsers}
                onEdit={handleEditUser}
                onDelete={handleDeleteUser}
                onEnableDisable={handleEnableDisable}
                deletingUserId={deletingUserId}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />
            </div>
          </main>

          {/* User Modal */}
          <UserModal
            isOpen={isModalOpen}
            onClose={() => {
              logger.debug('[WALRUS] AdminUsersPage: closing modal');
              setIsModalOpen(false);
            }}
            mode={modalMode}
            user={selectedUser}
            onSubmit={handleModalSubmit}
            isLoading={isSubmitting}
          />
        </div>
      </Layout>
    </AdminRoute>
  );
}
